import React, { useState } from 'react';
import { scoreRanges } from '../mock';
import PageHero from '../components/shared/PageHero';
import CtaBlock from '../components/shared/CtaBlock';
import OfferList from '../components/shared/OfferList';
import { DollarSign, Award, Check } from 'lucide-react';

const benefits = [
  { icon: DollarSign, title: 'Save Thousands', desc: 'Improving your score from poor to good can save $50,000+ in interest on a typical 30-year mortgage. Compare tools to lift your score.' },
  { icon: Check, title: 'Unlock Better Approvals', desc: 'A good credit score makes you 5× more likely to be approved for loans, cards, and rentals with favorable terms.' },
  { icon: Award, title: 'Higher Credit Limits', desc: 'Excellent-credit consumers receive 3–4× higher credit limits than fair-credit peers – more flexibility, less stress.' }
];

export default function BuildCredit() {
  const [activeRange, setActiveRange] = useState(scoreRanges[3]);

  return (
    <>
      <PageHero
        eyebrow="Compare Financial Growth Products"
        title={<>Build Your <span className="text-emerald-600">Credit</span> & Grow Your Wealth</>}
        subtitle="Compare credit builders, high-yield savings, and beginner investing platforms – all editor-ranked for real-world results."
        ctaLabel="View All Products"
        ctaTo="/build-credit#offers"
        badges={[
          { title: '+35 points', desc: 'Score Improvement' },
          { title: '8 days', desc: 'Fast Results' },
          { title: 'Editor-Vetted', desc: 'Providers' }
        ]}
      />

      {/* Why matters */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-slate-900 font-bold">Why Compare Credit & Wealth Products</h2>
            <p className="mt-4 text-slate-600">A better score and smarter savings open real doors – lower rates, higher limits, and long-term wealth.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="rounded-2xl border border-slate-200 bg-white p-7 hover:border-emerald-300 hover:shadow-lg transition-all group">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center group-hover:bg-emerald-600 transition-colors">
                  <b.icon className="w-6 h-6 text-emerald-700 group-hover:text-white transition-colors" />
                </div>
                <h3 className="mt-5 text-lg font-bold text-slate-900">{b.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Score explorer */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-slate-900 font-bold">Where Does Your Credit Score Stand?</h2>
            <p className="mt-4 text-slate-600">Click a range to see product recommendations that fit your credit tier.</p>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-6 lg:p-8 shadow-sm">
            <div className="flex flex-wrap gap-2 mb-6">
              {scoreRanges.map((r) => (
                <button key={r.label} onClick={() => setActiveRange(r)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeRange.label === r.label
                      ? 'bg-emerald-600 text-white shadow'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}>
                  {r.label}
                </button>
              ))}
            </div>
            <div className="relative h-3 rounded-full bg-gradient-to-r from-red-400 via-amber-400 via-yellow-400 via-lime-500 to-emerald-500 mb-2" />
            <div className="flex justify-between text-xs text-slate-500 mb-8">
              <span>300</span><span>580</span><span>670</span><span>740</span><span>800+</span>
            </div>
            <div className={`rounded-xl p-6 bg-gradient-to-br ${activeRange.color} text-white`}>
              <div className="text-xs uppercase tracking-widest font-semibold opacity-90">{activeRange.min} – {activeRange.max}</div>
              <h3 className="font-serif-display text-3xl font-bold mt-2">{activeRange.label} Credit</h3>
              <p className="mt-3 leading-relaxed max-w-2xl opacity-95">{activeRange.desc}</p>
            </div>
            <p className="mt-6 text-center text-slate-600 text-sm">Alliance readers move up an average of 120 points in 12 months by comparing and using the right products.</p>
          </div>
        </div>
      </section>

      {/* Offer list */}
      <section id="offers" className="py-20 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-4">Editor-Ranked</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-slate-900 font-bold">Compare Credit Builders, Savings & Investing</h2>
            <p className="mt-4 text-slate-600">Click any banner or button to visit the provider. Offers refreshed regularly by our editorial team.</p>
          </div>
          <OfferList category="financials" empty="No financial products available yet. Check back soon." />
        </div>
      </section>

      <CtaBlock title="Grow Your Financial Future Today" subtitle="Alliance readers unlock exclusive rates on savings, credit builders, and investing platforms." ctaLabel="Compare Products" ctaTo="/build-credit#offers" />
    </>
  );
}
